import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

function extractPermissions(userData) {
  const role = userData?.role;
  if (!role || typeof role === 'string') return [];
  return (role.permissions ?? []).map((p) => (typeof p === 'string' ? p : p.key));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  function applyUser(data) {
    setUser(data);
    setPermissions(extractPermissions(data));
  }

  async function refreshUser() {
    const { data } = await api.get('/users/me');
    applyUser(data);
    return data;
  }

  useEffect(() => {
    api.get('/users/me')
      .then((res) => applyUser(res.data))
      .catch(() => { setUser(null); setPermissions([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    function handleUnauthorized() {
      setUser(null);
      setPermissions([]);
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Role/permission edits made elsewhere (another tab, an admin editing roles)
  // only take effect server-side immediately — resync when the user comes
  // back to this tab so the UI doesn't keep showing stale nav/permissions.
  useEffect(() => {
    function handleFocus() {
      if (user) refreshUser().catch(() => {});
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function login(email, password) {
    await api.post('/auth/login', { email, password });
    return refreshUser();
  }

  async function logout() {
    try { await api.post('/auth/logout'); } catch (_) { /* clear local state regardless */ }
    setUser(null);
    setPermissions([]);
  }

  async function impersonate(companyId) {
    await api.post(`/auth/impersonate/${companyId}`);
    return refreshUser();
  }

  async function stopImpersonation() {
    await api.post('/auth/stop-impersonation');
    return refreshUser();
  }

  const isImpersonating = user?.viewAsAdmin === true;

  const hasPermission = useCallback(
    (key) => permissions.includes(key),
    [permissions],
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, permissions, hasPermission, impersonate, stopImpersonation, isImpersonating, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
